import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config/api';
import AuthService from '../services/authService';
import TokenManager from '../services/TokenManager';
import { installFetchInterceptor, installAccountRefusalAnnouncer, ACCOUNT_REFUSAL_EVENT } from '../utils/fetchInterceptor';
import SettingsService from '../services/settingsService';
import NotificationService from '../services/notificationService';
import PushSubscriptionService from '../services/pushSubscriptionService';
import { CURRENT_TERMS_VERSION } from '../config/termsVersion';
import { invalidateSessionCaches } from '../utils/cacheInvalidation';
import i18n from '../i18n';
import { getStoredLanguage, isSupportedLanguage, setStoredLanguage } from '../utils/languagePreference';

// Send the browser's IANA timezone to the server once per account per tab, on
// first successful auth. Used to schedule the nightly artist alerts and trip
// briefs in the user's local evening. Best-effort: only latches on a 2xx so a
// transient server error gets retried on the next call. Keyed by the account,
// so the next account signed in on this tab sends its own (BH-10-08).
let _tzSyncedFor = null;
async function syncTimezoneOnce(userId) {
  if (!userId || _tzSyncedFor === userId) return;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    const res = await fetch(`${API_BASE}/api/auth/timezone`, {
      method: 'PUT',
      headers: AuthService.getJsonHeaders(),
      body: JSON.stringify({ timezone: tz })
    });
    if (res.ok) _tzSyncedFor = userId;
  } catch (e) { /* best effort - leave the latch unset to retry */ }
}

// Signing out stops this device's push for the account (BH-06-01): the push
// subscription belongs to the device, not the session, so without this the
// next person on a shared browser keeps getting the previous account's DM
// previews. Best-effort and bounded: push trouble (no service worker, a slow
// push service, a dead network) must never hold up the sign-out. After
// signing back in, push is re-enabled by hand from Settings.
const PUSH_SIGN_OUT_TIMEOUT_MS = 3000;
function unsubscribeDevicePush(options) {
  let timer;
  const giveUp = new Promise((resolve) => { timer = setTimeout(resolve, PUSH_SIGN_OUT_TIMEOUT_MS); });
  const attempt = Promise.resolve()
    .then(() => PushSubscriptionService.disablePush(options))
    .catch(() => null);
  return Promise.race([attempt, giveUp]).finally(() => clearTimeout(timer));
}

// Create the Auth Context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// The same context, or null outside an AuthProvider: for leaf components
// (the report forms) that read one field and are also rendered on their own.
export const useOptionalAuth = () => useContext(AuthContext) || null;

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const { t } = useTranslation('auth');

  // State for user authentication
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState('user');
  const [authLoading, setAuthLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(t('location.notSet'));

  // State to trigger Settings tour after signup

  // Terms acceptance state
  const [termsOutdated, setTermsOutdated] = useState(false);

  // Enable dual-token system synchronously (before any effects run)
  const dualTokenInitialized = useRef(false);
  if (!dualTokenInitialized.current) {
    dualTokenInitialized.current = true;
    // Before the token interceptor, so its 401 retries are watched too.
    installAccountRefusalAnnouncer();
    const dualTokenEnabled = process.env.REACT_APP_DUAL_TOKEN_ENABLED === 'true';
    if (dualTokenEnabled) {
      TokenManager.enable();
      installFetchInterceptor(TokenManager);
    }
  }

  // Load user location
  const loadUserLocation = useCallback(async (user) => {
    try {
      if (user) {
        const location = await SettingsService.getHomeLocation(user.id);
        setCurrentLocation(location || t('location.notSet'));
      } else {
        setCurrentLocation(t('location.notSet'));
      }
    } catch (error) {
      console.error('Error loading user location:', error);
      setCurrentLocation(t('location.notSet'));
    }
  }, [t]);

  // Apply the account-level language preference once auth resolves. This is the
  // top of the precedence chain (see languagePreference.js) but needs an async
  // fetch, so it can't run in i18n's synchronous init. Best-effort:
  //  - account has a saved language → adopt it + mirror to this device.
  //  - account has none yet → push this device's explicit choice up, so a
  //    language picked while signed-out carries into the fresh profile.
  const reconcileLanguage = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const serverLng = await SettingsService.getLanguage(userId);
      if (serverLng && isSupportedLanguage(serverLng)) {
        if (i18n.language !== serverLng) await i18n.changeLanguage(serverLng);
        setStoredLanguage(serverLng);
      } else {
        const localLng = getStoredLanguage();
        if (localLng && isSupportedLanguage(localLng)) {
          SettingsService.updateLanguage(localLng, userId).catch(() => {});
        }
      }
    } catch (e) {
      /* best-effort — device localStorage/detection already set the language */
    }
  }, []);

  // Check admin status
  const checkAdminStatus = useCallback(async () => {
    try {
      if (!AuthService.isAuthenticated()) {
        setIsAdmin(false);
        setAdminRole('user');
        return;
      }

      const response = await fetch(`${API_BASE}/api/admin/check`, {
        headers: AuthService.getJsonHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        setIsAdmin(result.isAdmin || false);
        setAdminRole(result.role || 'user');
      } else {
        setIsAdmin(false);
        setAdminRole('user');
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setAdminRole('user');
    }
  }, []);

  // Initialize authentication state
  const initializeAuth = useCallback(async () => {
    setAuthLoading(true);
    try {
      // On page reload with dual-token, the in-memory access token is gone
      // but the HttpOnly refresh cookie persists — refresh to get a new access token
      if (TokenManager.isEnabled() && !TokenManager.accessToken) {
        await TokenManager.refreshAccessToken();
      }

      const user = await AuthService.getCurrentUser();

      if (user) {
        setCurrentUser(user);
        // Deliberately not awaited: location and admin status only feed
        // secondary UI (nav location label, admin menu items) and must not
        // hold up first render on a cold start. Both swallow their own errors.
        loadUserLocation(user);
        checkAdminStatus();
        reconcileLanguage(user.id);
        // Check if terms are outdated
        setTermsOutdated(user.termsVersion !== CURRENT_TERMS_VERSION);
        syncTimezoneOnce(user.id);
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setAdminRole('user');
        setCurrentLocation(t('location.notSet'));
        setTermsOutdated(false);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      setCurrentUser(null);
      setIsAdmin(false);
      setAdminRole('user');
      setCurrentLocation(t('location.notSet'));
      setTermsOutdated(false);
    } finally {
      setAuthLoading(false);
    }
  }, [loadUserLocation, checkAdminStatus, reconcileLanguage, t]);

  // The account's moderation state (policy §4) for AccountStateBanner. Only
  // /api/auth/me carries it, and the sign-in responses do not, so it is read
  // quietly after a sign-in, when a write is refused, when the tab comes
  // back into view (an action may have been taken, undone or have ended),
  // and when a report form opens (a report pause, C156).
  // Merged into the user, never replacing it; a failed read changes nothing
  // (unlike getCurrentUser, it never clears the token).
  const lastAccountRefreshRef = useRef(0);
  const refreshAccountState = useCallback(async () => {
    // At most one read every few seconds: a restricted person's recap
    // autosave announces its skipped widening on every save.
    if (Date.now() - lastAccountRefreshRef.current < 5000) return;
    lastAccountRefreshRef.current = Date.now();
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, { headers: AuthService.getJsonHeaders() });
      if (!response.ok) return;
      const data = await response.json();
      const fresh = data?.success ? data.user : null;
      if (!fresh) return;
      // A report pause (arc 6) rides along: the report forms read it.
      setCurrentUser(prev => (prev && prev.id === fresh.id
        && (prev.accountState !== fresh.accountState || prev.accountStateUntil !== fresh.accountStateUntil
          || (prev.reportsPausedUntil || null) !== (fresh.reportsPausedUntil || null))
        ? {
          ...prev,
          accountState: fresh.accountState,
          accountStateUntil: fresh.accountStateUntil,
          reportsPausedUntil: fresh.reportsPausedUntil || null
        }
        : prev));
    } catch { /* offline: keep what we have */ }
  }, []);

  const signedIn = !!currentUser;
  useEffect(() => {
    if (!signedIn) return undefined;
    const onVisible = () => { if (document.visibilityState === 'visible') refreshAccountState(); };
    window.addEventListener(ACCOUNT_REFUSAL_EVENT, refreshAccountState);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(ACCOUNT_REFUSAL_EVENT, refreshAccountState);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn, refreshAccountState]);

  // Login function
  const login = async (email, password, remember = true) => {
    try {
      const result = await AuthService.signIn(email, password, remember);

      if (result.success) {
        setCurrentUser(result.user);
        refreshAccountState();
        await loadUserLocation(result.user);
        await checkAdminStatus();
        reconcileLanguage(result.user.id);
        syncTimezoneOnce(result.user.id);
        return { success: true, message: result.message };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: t('loginFailed') };
    }
  };

  // Signup function
  const signup = async (userData) => {
    try {
      const result = await AuthService.signUp(userData);

      if (result.success) {
        setCurrentUser(result.user);
        await loadUserLocation(result.user);
        // New users are not admin by default
        setIsAdmin(false);
        setAdminRole('user');
        // Evaluate terms acceptance for the new user
        setTermsOutdated(result.user.termsVersion !== CURRENT_TERMS_VERSION);
        return { success: true, user: result.user, message: result.message };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: t('signupFailed') };
    }
  };

  // Accept terms of service
  const acceptTerms = useCallback(() => {
    setTermsOutdated(false);
    // Update the local user object
    setCurrentUser(prev => prev ? { ...prev, termsVersion: CURRENT_TERMS_VERSION, termsAcceptedAt: new Date().toISOString() } : prev);
  }, []);

  // Track whether the user was actually logged in, so the token-expired
  // callback can distinguish a real session expiry from an initial-load
  // refresh failure for a never-logged-in visitor.
  const hadActiveSessionRef = useRef(false);
  useEffect(() => {
    if (currentUser) hadActiveSessionRef.current = true;
  }, [currentUser]);

  // Logout function
  const logout = async () => {
    NotificationService.cleanup();
    // Before signOut: the server half of the unsubscribe needs the token.
    await unsubscribeDevicePush();
    await AuthService.signOut();
    // Auth boundary: drop every session-scoped module cache so nothing cached
    // for this account (likes, saves, follows, friend-gated data) can render
    // for the next account on this device.
    invalidateSessionCaches();
    hadActiveSessionRef.current = false;
    setCurrentUser(null);
    setIsAdmin(false);
    setAdminRole('user');
    setCurrentLocation(t('location.notSet'));
  };

  // Update location when settings change
  const updateLocation = useCallback(async () => {
    if (currentUser) {
      await loadUserLocation(currentUser);
    }
  }, [currentUser, loadUserLocation]);

  // Register token-expired callback (needs state setters, so must be in effect)
  useEffect(() => {
    if (TokenManager.isEnabled()) {
      TokenManager.onTokenExpiredCallback(({ rejected = false } = {}) => {
        const wasLoggedIn = hadActiveSessionRef.current;
        // Auth boundary: same cache flush as logout (see there for rationale).
        invalidateSessionCaches();
        setCurrentUser(null);
        setIsAdmin(false);
        setAdminRole('user');
        setCurrentLocation(t('location.notSet'));

        // Only redirect if there was an active session that just died — not
        // on initial-load refresh failures for logged-out visitors.
        if (!wasLoggedIn) return;
        hadActiveSessionRef.current = false;

        console.warn('Session expired. Redirecting to sign-in.');

        // A session the server rejected (401) is over, so this device's push
        // goes too, as on logout (BH-06-01). The token is already gone, so
        // only the browser half runs: a dropped subscription's endpoint
        // answers 404/410 and the server revokes its row on the next send.
        // Awaited (bounded) so the redirect doesn't unload the page
        // mid-unsubscribe. A refresh that failed on the network or a 5xx
        // keeps push (Kurtis, 2026-10-16): the session may still be good.
        const dropPush = rejected ? unsubscribeDevicePush({ tellServer: false }) : Promise.resolve();
        dropPush.then(() => {
          // Hard reload (not React Router navigate) flushes any stale data
          // components fetched before the session died.
          const path = window.location.pathname;
          const publicAuthPaths = ['/signin', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
          if (!publicAuthPaths.some(p => path.startsWith(p))) {
            const returnTo = encodeURIComponent(path + window.location.search);
            window.location.href = `/signin?returnTo=${returnTo}&reason=expired`;
          }
        });
      });
    }
  }, [t]);

  // Proactive token refresh timer (keeps access token fresh)
  useEffect(() => {
    if (!TokenManager.isEnabled()) return;

    const refreshInterval = setInterval(async () => {
      if (TokenManager.accessToken) {
        await TokenManager.getValidAccessToken();
      }
    }, 4 * 60 * 1000); // Every 4 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Listen for settings updates
  useEffect(() => {
    const handleSettingsUpdate = () => {
      updateLocation();
    };

    window.addEventListener('settingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, [updateLocation]);

  // Auth context value
  const value = {
    // State
    currentUser,
    isAdmin,
    adminRole,
    authLoading,
    currentLocation,
    isAuthenticated: !!currentUser,
    termsOutdated,

    // Actions
    login,
    signup,
    logout,
    initializeAuth,
    updateLocation,
    acceptTerms,
    refreshAccountState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};